import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Smile, Frown, Meh, Sparkles } from 'lucide-react';

// Theme colors - soft, professional palette
const COLORS = {
  primary: '#5DBFAD',
  primarySoft: 'rgba(93, 191, 173, 0.12)',
  accent: '#F5A623',
  accentSoft: 'rgba(245, 166, 35, 0.12)',
  coral: '#E8795E',
  coralSoft: 'rgba(232, 121, 94, 0.12)',
  text: '#2C3E50',
  textSecondary: '#6B7280',
  border: '#E5E7EB',
  background: '#F8FAFB',
  white: '#FFFFFF'
};

const EmotionSelector = ({ onComplete }) => {
  const [selectedMood, setSelectedMood] = useState('');
  const [showMessage, setShowMessage] = useState(false);

  const emotions = [
    { 
      id: 'happy', 
      label: 'Happy', 
      color: COLORS.primary, 
      bgColor: COLORS.primarySoft,
      Icon: Smile
    },
    { 
      id: 'sad', 
      label: 'Sad', 
      color: COLORS.coral, 
      bgColor: COLORS.coralSoft,
      Icon: Frown
    },
    { 
      id: 'meh', 
      label: 'Meh', 
      color: COLORS.accent, 
      bgColor: COLORS.accentSoft,
      Icon: Meh
    }
  ];

  const messages = {
    happy: "Clearly the day is going your way! You got gifted 10 stars",
    sad: "You just earned your first 10 stars, hope that brightens your day",
    meh: "Let's change that, you got gifted 10 stars, for being here despite all odds!"
  };

  const handleSubmit = () => {
    if (!selectedMood) return;
    
    setShowMessage(true);
    setTimeout(() => {
      onComplete(selectedMood);
    }, 2000);
  };

  // Sophisticated face SVG based on mood
  const getFaceComponents = (mood) => {
    const faces = {
      happy: {
        eyes: (
          <>
            <motion.circle
              cx="80" cy="95"
              r="6"
              fill="#4A5568"
              animate={{ scaleY: [1, 0.3, 1] }}
              transition={{ duration: 3, repeat: Infinity, repeatDelay: 4 }}
            />
            <motion.circle
              cx="160" cy="95"
              r="6"
              fill="#4A5568"
              animate={{ scaleY: [1, 0.3, 1] }}
              transition={{ duration: 3, repeat: Infinity, repeatDelay: 4 }}
            />
            {/* Subtle eye highlights */}
            <circle cx="82" cy="92" r="2" fill="#FFFFFF" opacity="0.8" />
            <circle cx="162" cy="92" r="2" fill="#FFFFFF" opacity="0.8" />
          </>
        ),
        mouth: (
          <motion.path
            d="M 75 140 Q 120 165 165 140"
            stroke="#4A5568"
            strokeWidth="4"
            fill="none"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
          />
        ),
        eyebrows: (
          <>
            <motion.path
              d="M 65 75 Q 80 70 95 75"
              stroke="#4A5568"
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
            <motion.path
              d="M 145 75 Q 160 70 175 75"
              stroke="#4A5568"
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </>
        )
      },
      sad: {
        eyes: (
          <>
            <circle cx="80" cy="95" r="6" fill="#4A5568" />
            <circle cx="160" cy="95" r="6" fill="#4A5568" />
            <circle cx="82" cy="92" r="2" fill="#FFFFFF" opacity="0.6" />
            <circle cx="162" cy="92" r="2" fill="#FFFFFF" opacity="0.6" />
          </>
        ),
        mouth: (
          <motion.path
            d="M 75 155 Q 120 135 165 155"
            stroke="#4A5568"
            strokeWidth="4"
            fill="none"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
          />
        ),
        eyebrows: (
          <>
            <motion.path
              d="M 65 82 Q 80 75 95 82"
              stroke="#4A5568"
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
            <motion.path
              d="M 145 82 Q 160 75 175 82"
              stroke="#4A5568"
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </>
        )
      },
      meh: {
        eyes: (
          <>
            <circle cx="80" cy="95" r="6" fill="#4A5568" />
            <circle cx="160" cy="95" r="6" fill="#4A5568" />
            <circle cx="82" cy="92" r="2" fill="#FFFFFF" opacity="0.7" />
            <circle cx="162" cy="92" r="2" fill="#FFFFFF" opacity="0.7" />
          </>
        ),
        mouth: (
          <motion.line
            x1="80" y1="150"
            x2="160" y2="150"
            stroke="#4A5568"
            strokeWidth="4"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
          />
        ),
        eyebrows: (
          <>
            <motion.line
              x1="65" y1="78"
              x2="95" y2="78"
              stroke="#4A5568"
              strokeWidth="3"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
            <motion.line
              x1="145" y1="78"
              x2="175" y2="78"
              stroke="#4A5568"
              strokeWidth="3"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </>
        )
      }
    };
    
    return faces[mood] || faces.meh;
  };

  const currentFace = selectedMood ? getFaceComponents(selectedMood) : getFaceComponents('meh');
  const selectedEmotion = emotions.find(e => e.id === selectedMood);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="onboarding-step emotion-selector"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        background: COLORS.background
      }}
    >
      <h2 className="step-title" style={{ 
        fontSize: '26px', 
        fontWeight: '700', 
        marginBottom: '8px',
        color: COLORS.text,
        textAlign: 'center'
      }}>
        Welcome to socialstar
      </h2>
      <p className="step-subtitle" style={{ 
        fontSize: '15px', 
        color: COLORS.textSecondary, 
        marginBottom: '30px',
        textAlign: 'center' 
      }}>
        How are you feeling today?
      </p>

      {/* Animated Face */}
      <motion.div
        className="emotion-face"
        animate={{
          scale: selectedMood ? [1, 1.03, 1] : 1,
          y: [0, -2, 0]
        }}
        transition={{ 
          scale: { duration: 0.6 },
          y: { duration: 4, repeat: Infinity, ease: "easeInOut" }
        }}
        style={{ marginBottom: '40px' }}
      >
        <svg width="180" height="180" viewBox="0 0 240 240" style={{
          filter: 'drop-shadow(0 6px 24px rgba(0,0,0,0.08))'
        }}>
          {/* Face Circle with soft fill */}
          <motion.circle
            cx="120" cy="120" r="110"
            fill={selectedEmotion ? selectedEmotion.bgColor : COLORS.primarySoft}
            stroke={selectedEmotion ? selectedEmotion.color : COLORS.primary}
            strokeWidth="2"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', damping: 20, stiffness: 200 }}
          />
          
          {/* Face Components */}
          {currentFace.eyebrows}
          {currentFace.eyes}
          {currentFace.mouth}
        </svg>
      </motion.div>

      {/* Emotion Cards - Horizontal Layout with Icons */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '40px',
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        {emotions.map((emotion, index) => {
          const IconComponent = emotion.Icon;
          return (
            <motion.div
              key={emotion.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
              onClick={() => setSelectedMood(emotion.id)}
              style={{
                width: '100px',
                height: '100px',
                background: selectedMood === emotion.id 
                  ? emotion.bgColor
                  : COLORS.white,
                border: selectedMood === emotion.id 
                  ? `2px solid ${emotion.color}` 
                  : `2px solid ${COLORS.border}`,
                borderRadius: '16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: selectedMood === emotion.id 
                  ? `0 6px 20px rgba(0,0,0,0.1)`
                  : '0 2px 8px rgba(0,0,0,0.04)'
              }}
            >
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                background: selectedMood === emotion.id ? 'rgba(255,255,255,0.7)' : emotion.bgColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '8px'
              }}>
                <IconComponent 
                  size={24} 
                  color={emotion.color}
                  strokeWidth={2}
                />
              </div>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: selectedMood === emotion.id ? emotion.color : COLORS.textSecondary
              }}>
                {emotion.label}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Submit Button */}
      {selectedMood && !showMessage && (
        <motion.button
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={handleSubmit}
          style={{
            background: COLORS.primary,
            color: 'white',
            border: 'none',
            borderRadius: '14px',
            padding: '14px 40px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(93, 191, 173, 0.3)'
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Continue
        </motion.button>
      )}

      {/* Message Display */}
      {showMessage && (
        <motion.div
          initial={{ opacity: 0, y: 15, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            textAlign: 'center',
            fontSize: '15px',
            color: COLORS.text,
            background: COLORS.white,
            padding: '20px 24px',
            borderRadius: '14px',
            border: `1px solid ${COLORS.border}`,
            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
            maxWidth: '360px'
          }}
        >
          <Sparkles size={24} color={COLORS.accent} />
          <span>{messages[selectedMood]}</span>
        </motion.div>
      )}
    </motion.div>
  );
};

export default EmotionSelector;
